import type { AgentMessage, AgentSettings, AgentStreamEvent } from '@crafty-companion/shared';
import { getCsrfToken, ApiClientError } from './api';

async function agentRequest<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  if (init.body) {
    headers.set('content-type', 'application/json');
  }
  const csrf = getCsrfToken();
  if (init.method && init.method !== 'GET' && csrf) {
    headers.set('x-csrf-token', csrf);
  }
  const response = await fetch(path, { ...init, headers, credentials: 'same-origin' });
  const payload = (await response.json().catch(() => ({}))) as { error?: string };
  if (!response.ok) {
    throw new ApiClientError(payload.error ?? 'Request failed');
  }
  return payload as T;
}

export async function getAgentMessages(): Promise<{ messages: AgentMessage[] }> {
  return agentRequest<{ messages: AgentMessage[] }>('/api/agent/messages?limit=50');
}

export async function clearAgent(): Promise<void> {
  await agentRequest<void>('/api/agent/clear', { method: 'POST' });
}

export async function getAgentSettings(): Promise<AgentSettings> {
  return agentRequest<AgentSettings>('/api/agent/settings');
}

export async function setAgentEnabled(enabled: boolean): Promise<AgentSettings> {
  return agentRequest<AgentSettings>('/api/agent/settings', {
    method: 'PUT',
    body: JSON.stringify({ enabled }),
  });
}

export interface StreamHandlers {
  onMessage: (message: AgentMessage) => void;
  onToken: (text: string) => void;
  onToolStart: (e: { id: string; name: string; input: unknown }) => void;
  onToolEnd: (e: { id: string; ok: boolean; summary: string }) => void;
  onDone: () => void;
  onError: (message: string) => void;
}

export async function streamAgentMessage(
  message: string,
  model: string,
  handlers: StreamHandlers,
  signal: AbortSignal,
): Promise<void> {
  const headers = new Headers();
  headers.set('content-type', 'application/json');
  const csrf = getCsrfToken();
  if (csrf) {
    headers.set('x-csrf-token', csrf);
  }

  const response = await fetch('/api/agent/messages', {
    method: 'POST',
    headers,
    body: JSON.stringify({ message, model }),
    credentials: 'same-origin',
    signal,
  });

  if (!response.ok) {
    const payload = (await response.json().catch(() => ({}))) as { error?: string };
    const err = payload.error ?? 'Request failed';
    handlers.onError(err);
    handlers.onDone();
    throw new ApiClientError(err);
  }

  const body = response.body;
  if (!body) {
    handlers.onError('No response body');
    handlers.onDone();
    return;
  }

  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) {
        break;
      }
      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() ?? '';

      for (const line of lines) {
        const trimmed = line.trim();
        if (!trimmed) continue;
        if (trimmed.startsWith('data: ')) {
          const rawData = trimmed.slice(6);
          try {
            const event = JSON.parse(rawData) as AgentStreamEvent;
            switch (event.type) {
              case 'message':
                handlers.onMessage(event.message);
                break;
              case 'token':
                handlers.onToken(event.text);
                break;
              case 'tool_start':
                handlers.onToolStart({ id: event.id, name: event.name, input: event.input });
                break;
              case 'tool_end':
                handlers.onToolEnd({ id: event.id, ok: event.ok, summary: event.summary });
                break;
              case 'done':
                handlers.onDone();
                break;
              case 'error':
                handlers.onError(event.message);
                break;
            }
          } catch (e) {
            console.error('Failed to parse SSE event data', e, rawData);
          }
        }
      }
    }
  } catch (error: any) {
    if (error.name === 'AbortError') {
      handlers.onDone();
      return;
    }
    handlers.onError(error.message ?? 'Stream read error');
    handlers.onDone();
    throw error;
  }
}
